import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  useTheme,
  alpha,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { format, parseISO } from 'date-fns';

interface PercentileRankingsOverTimeProps {
  data: Array<{
    date: string;
    worldPercentile: number | null;
    continentPercentile: number | null;
    countryPercentile: number | null;
  }>;
  loading?: boolean;
  title?: string;
}

export const PercentileRankingsOverTime: React.FC<PercentileRankingsOverTimeProps> = ({
  data,
  loading = false,
  title = 'Percentile Rankings Over Time',
}) => {
  const theme = useTheme();
  const [hoveredLine, setHoveredLine] = useState<string | null>(null);

  // Colors for the three ranking levels (matching CurrentPercentileRankings)
  const colors = {
    world: '#003f9f',       // Blue for world
    continent: '#16d9bc',   // Teal for continent
    country: '#0c7876',     // Dark teal for country
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: 'background.paper',
            p: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 2,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => {
            if (entry.value === undefined || entry.value === null) return null;

            return (
              <Typography
                key={index}
                variant="body2"
                sx={{ color: entry.color }}
              >
                {entry.name}: {entry.value.toFixed(1)}
              </Typography>
            );
          }).filter(Boolean)}
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton variant="text" width="60%" height={32} />
          <Skeleton variant="rectangular" width="100%" height={350} sx={{ mt: 3 }} />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            {title}
          </Typography>
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="body2" color="text.secondary">
              No historical ranking data available for this market
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          {title}
        </Typography>

        <Box sx={{ width: '100%', height: 350 }}>
          <ResponsiveContainer>
            <LineChart
              data={data}
              margin={{ top: 5, right: 30, left: 50, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={alpha(theme.palette.divider, 0.5)}
                vertical={false}
              />

              {/* Add vertical lines only at January dates */}
              {data
                .filter(d => {
                  try {
                    return format(parseISO(d.date), 'MM') === '01';
                  } catch {
                    return false;
                  }
                })
                .map(d => (
                  <ReferenceLine
                    key={d.date}
                    x={d.date}
                    yAxisId="left"
                    stroke={alpha(theme.palette.divider, 0.5)}
                    strokeDasharray="3 3"
                  />
                ))
              }

              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                stroke={theme.palette.text.secondary}
                tickFormatter={(value) => {
                  try {
                    const date = parseISO(value);
                    // Only show January years
                    if (format(date, 'MM') === '01') {
                      return format(date, 'yyyy');
                    }
                    return '';
                  } catch {
                    return value;
                  }
                }}
                interval={0}
              />

              <YAxis
                yAxisId="left"
                domain={[0, 100]}
                tick={{ fontSize: 12 }}
                stroke={theme.palette.text.secondary}
                label={{
                  value: 'Percentile Ranking',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 12, textAnchor: 'middle' },
                }}
              />

              <Tooltip content={<CustomTooltip />} />

              <Legend
                wrapperStyle={{ fontSize: 12 }}
                iconType="line"
                onMouseEnter={(e: any) => {
                  if (e && e.value) {
                    setHoveredLine(e.value);
                  }
                }}
                onMouseLeave={() => {
                  setHoveredLine(null);
                }}
              />

              {/* World Percentile Line */}
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="worldPercentile"
                stroke={colors.world}
                strokeWidth={hoveredLine === 'World' ? 3 : 2}
                strokeOpacity={hoveredLine ? (hoveredLine === 'World' ? 1 : 0.2) : 1}
                dot={false}
                name="World"
                connectNulls={false}
              />

              {/* Continent Percentile Line */}
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="continentPercentile"
                stroke={colors.continent}
                strokeWidth={hoveredLine === 'Continent' ? 3 : 2}
                strokeOpacity={hoveredLine ? (hoveredLine === 'Continent' ? 1 : 0.2) : 1}
                dot={false}
                name="Continent"
                connectNulls={false}
              />

              {/* Country Percentile Line */}
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="countryPercentile"
                stroke={colors.country}
                strokeWidth={hoveredLine === 'Country' ? 3 : 2}
                strokeOpacity={hoveredLine ? (hoveredLine === 'Country' ? 1 : 0.2) : 1}
                dot={false}
                name="Country"
                connectNulls={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Percentile rankings show this market's performance relative to peers over time (0-100 scale)
        </Typography>
      </CardContent>
    </Card>
  );
};
